import { useMemo } from 'react';
import {
  Lightbulb,
  AlertTriangle,
  HelpCircle,
  TrendingUp,
  TrendingDown,
  BarChart3,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScoreBadge } from '@/features/evaluation/components/ScoreBar';
import type { EvaluationWithDetails } from '@/features/evaluation/types';

// =========================================
// CrossDocSynthesis — themes, contradictions, gaps
// =========================================

interface CrossDocSynthesisProps {
  evaluations: EvaluationWithDetails[];
  className?: string;
}

interface Theme {
  criteria_name: string;
  criteria_id: string;
  direction: 'high' | 'low';
  avg_score: number;
  source_count: number;
  agreement_pct: number;
}

interface Contradiction {
  criteria_name: string;
  criteria_id: string;
  high_source: string;
  high_score: number;
  low_source: string;
  low_score: number;
  spread: number;
}

interface Gap {
  criteria_name: string;
  criteria_id: string;
  missing_sources: string[];
  coverage_pct: number;
}

interface SynthesisData {
  themes: Theme[];
  contradictions: Contradiction[];
  gaps: Gap[];
  overallStats: {
    avgScore: number;
    scoreRange: [number, number];
    totalCriteria: number;
    totalSources: number;
  };
}

export function CrossDocSynthesis({ evaluations, className }: CrossDocSynthesisProps) {
  const synthesis = useMemo(() => computeSynthesis(evaluations), [evaluations]);

  if (evaluations.length < 2) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary stats */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-indigo-600" />
            <CardTitle className="text-base">Cross-Document Synthesis</CardTitle>
          </div>
          <p className="text-xs text-slate-500">
            Patterns, contradictions, and gaps across {synthesis.overallStats.totalSources} sources
            and {synthesis.overallStats.totalCriteria} criteria.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <StatBlock
              icon={<Lightbulb className="h-4 w-4 text-emerald-600" />}
              label="Common Themes"
              value={synthesis.themes.length}
              color="emerald"
            />
            <StatBlock
              icon={<AlertTriangle className="h-4 w-4 text-amber-600" />}
              label="Contradictions"
              value={synthesis.contradictions.length}
              color="amber"
            />
            <StatBlock
              icon={<HelpCircle className="h-4 w-4 text-rose-600" />}
              label="Gaps"
              value={synthesis.gaps.length}
              color="rose"
            />
          </div>
        </CardContent>
      </Card>

      {/* Themes */}
      {synthesis.themes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-emerald-600" />
              <CardTitle className="text-sm">Common Themes</CardTitle>
              <Badge variant="secondary" className="text-[10px] ml-auto">
                {synthesis.themes.length}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">
              Criteria where most sources agree in scoring direction.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {synthesis.themes.map((theme) => (
              <div
                key={theme.criteria_id}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border"
              >
                {theme.direction === 'high' ? (
                  <TrendingUp className="h-4 w-4 text-emerald-500 shrink-0" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-rose-500 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {theme.criteria_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {theme.source_count} sources &middot;{' '}
                    {theme.agreement_pct}% agreement &middot;{' '}
                    {theme.direction === 'high' ? 'consistently strong' : 'consistently weak'}
                  </p>
                </div>
                <ScoreBadge score={theme.avg_score} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Contradictions */}
      {synthesis.contradictions.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <CardTitle className="text-sm">Contradictions</CardTitle>
              <Badge variant="secondary" className="text-[10px] ml-auto">
                {synthesis.contradictions.length}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">
              Criteria with large score differences between sources.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {synthesis.contradictions.map((c) => (
              <div
                key={c.criteria_id}
                className="p-2.5 rounded-lg bg-amber-50/60 border border-amber-200/60"
              >
                <p className="text-sm font-medium text-slate-700">{c.criteria_name}</p>
                <div className="flex items-center gap-2 mt-1.5 text-xs">
                  <span className="text-emerald-600 font-medium truncate max-w-[120px]">
                    {c.high_source}
                  </span>
                  <ScoreBadge score={c.high_score} />
                  <span className="text-slate-400">vs</span>
                  <span className="text-rose-600 font-medium truncate max-w-[120px]">
                    {c.low_source}
                  </span>
                  <ScoreBadge score={c.low_score} />
                  <Badge variant="outline" className="text-[10px] ml-auto shrink-0">
                    {c.spread.toFixed(0)}pt spread
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Gaps */}
      {synthesis.gaps.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-rose-600" />
              <CardTitle className="text-sm">Coverage Gaps</CardTitle>
              <Badge variant="secondary" className="text-[10px] ml-auto">
                {synthesis.gaps.length}
              </Badge>
            </div>
            <p className="text-xs text-slate-500">
              Criteria with incomplete data across sources.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {synthesis.gaps.map((gap) => (
              <div
                key={gap.criteria_id}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-rose-50/60 border border-rose-200/60"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {gap.criteria_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    Missing from:{' '}
                    {gap.missing_sources.slice(0, 3).join(', ')}
                    {gap.missing_sources.length > 3 &&
                      ` +${gap.missing_sources.length - 3} more`}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    'text-[10px] shrink-0',
                    gap.coverage_pct < 50 && 'border-rose-300 text-rose-600'
                  )}
                >
                  {gap.coverage_pct}% coverage
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* No findings */}
      {synthesis.themes.length === 0 &&
        synthesis.contradictions.length === 0 &&
        synthesis.gaps.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center">
              <BarChart3 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">
                No significant patterns detected across the selected sources.
              </p>
            </CardContent>
          </Card>
        )}
    </div>
  );
}

// =========================================
// StatBlock — compact stat display
// =========================================

interface StatBlockProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'emerald' | 'amber' | 'rose';
}

function StatBlock({ icon, label, value, color }: StatBlockProps) {
  return (
    <div
      className={cn(
        'p-3 rounded-lg border text-center',
        color === 'emerald' && 'bg-emerald-50/60 border-emerald-200/60',
        color === 'amber' && 'bg-amber-50/60 border-amber-200/60',
        color === 'rose' && 'bg-rose-50/60 border-rose-200/60'
      )}
    >
      <div className="flex items-center justify-center mb-1">{icon}</div>
      <p className="text-xl font-bold text-slate-900">{value}</p>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
    </div>
  );
}

// =========================================
// Computation helpers
// =========================================

function computeSynthesis(evaluations: EvaluationWithDetails[]): SynthesisData {
  const totalSources = evaluations.length;
  const scores = evaluations.map((ev) => ev.breakdown?.percentage ?? 0);
  const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;

  // Source name lookup
  const sourceNames = new Map<string, string>();
  for (const ev of evaluations) {
    sourceNames.set(
      ev.source_id,
      ev.source?.title || ev.source?.file_name || 'Untitled'
    );
  }

  // Collect per-criteria score arrays
  const criteriaScores = new Map<
    string,
    { name: string; scores: Array<{ source_id: string; pct: number }> }
  >();

  for (const ev of evaluations) {
    if (!ev.breakdown?.weighted_scores) continue;
    for (const ws of ev.breakdown.weighted_scores) {
      if (!criteriaScores.has(ws.criteria_id)) {
        criteriaScores.set(ws.criteria_id, { name: ws.criteria_name, scores: [] });
      }
      const pct = ws.max_weighted > 0
        ? (ws.weighted_score / ws.max_weighted) * 100
        : 0;
      criteriaScores.get(ws.criteria_id)!.scores.push({
        source_id: ev.source_id,
        pct,
      });
    }
  }

  // Identify themes (criteria where >=70% of sources agree on high/low)
  const themes: Theme[] = [];
  const contradictions: Contradiction[] = [];
  const gaps: Gap[] = [];

  const HIGH_THRESHOLD = 60;
  const LOW_THRESHOLD = 40;
  const AGREEMENT_THRESHOLD = 0.7;
  const CONTRADICTION_SPREAD = 40;

  for (const [criteriaId, data] of criteriaScores.entries()) {
    const { name, scores: criteriaEntries } = data;

    // Gaps: criteria not present in all sources
    if (criteriaEntries.length < totalSources) {
      const coveredIds = new Set(criteriaEntries.map((s) => s.source_id));
      const missing = evaluations
        .filter((ev) => !coveredIds.has(ev.source_id))
        .map((ev) => sourceNames.get(ev.source_id) || 'Unknown');
      gaps.push({
        criteria_name: name,
        criteria_id: criteriaId,
        missing_sources: missing,
        coverage_pct: Math.round((criteriaEntries.length / totalSources) * 100),
      });
    }

    if (criteriaEntries.length < 2) continue;

    const pcts = criteriaEntries.map((e) => e.pct);
    const avg = pcts.reduce((a, b) => a + b, 0) / pcts.length;
    const max = Math.max(...pcts);
    const min = Math.min(...pcts);

    // Themes: agreement on direction
    const highCount = pcts.filter((p) => p >= HIGH_THRESHOLD).length;
    const lowCount = pcts.filter((p) => p < LOW_THRESHOLD).length;
    const highAgreement = highCount / pcts.length;
    const lowAgreement = lowCount / pcts.length;

    if (highAgreement >= AGREEMENT_THRESHOLD) {
      themes.push({
        criteria_name: name,
        criteria_id: criteriaId,
        direction: 'high',
        avg_score: Math.round(avg),
        source_count: pcts.length,
        agreement_pct: Math.round(highAgreement * 100),
      });
    } else if (lowAgreement >= AGREEMENT_THRESHOLD) {
      themes.push({
        criteria_name: name,
        criteria_id: criteriaId,
        direction: 'low',
        avg_score: Math.round(avg),
        source_count: pcts.length,
        agreement_pct: Math.round(lowAgreement * 100),
      });
    }

    // Contradictions: large spread between best and worst
    const spread = max - min;
    if (spread >= CONTRADICTION_SPREAD) {
      const highEntry = criteriaEntries.reduce((a, b) => (a.pct > b.pct ? a : b));
      const lowEntry = criteriaEntries.reduce((a, b) => (a.pct < b.pct ? a : b));
      contradictions.push({
        criteria_name: name,
        criteria_id: criteriaId,
        high_source: sourceNames.get(highEntry.source_id) || 'Unknown',
        high_score: Math.round(highEntry.pct),
        low_source: sourceNames.get(lowEntry.source_id) || 'Unknown',
        low_score: Math.round(lowEntry.pct),
        spread,
      });
    }
  }

  // Sort by significance
  themes.sort((a, b) => b.agreement_pct - a.agreement_pct);
  contradictions.sort((a, b) => b.spread - a.spread);
  gaps.sort((a, b) => a.coverage_pct - b.coverage_pct);

  return {
    themes,
    contradictions,
    gaps,
    overallStats: {
      avgScore: Math.round(avgScore),
      scoreRange: [Math.min(...scores, 0), Math.max(...scores, 0)],
      totalCriteria: criteriaScores.size,
      totalSources,
    },
  };
}
